import moment from "moment";
import { AxiosPrivado } from "../axios/Axios";
import { leerLote, guardarLote } from "./cacheLocal";

/**
 * Carga un rango de fechas largo en ventanas mensuales, una a la vez.
 *
 * POR QUE ASI
 * -----------
 * El endpoint de filtrado no pagina y trae 10 relaciones por registro, asi
 * que una consulta de 3 meses devuelve ~3 MB y obliga al servidor a
 * instanciar decenas de miles de objetos. Node es de un solo hilo: durante
 * esos segundos la API no le responde a nadie mas.
 *
 * Fragmentar NO reduce el trabajo total del servidor — medido, sale ~44%
 * mas lento en tiempo de pared. Lo que hace es bajar el PICO: cada peticion
 * toca un tercio de los datos y el servidor respira entre una y otra, asi
 * que el resto de los usuarios sigue siendo atendido.
 *
 * Las ventanas ya cerradas (meses pasados) se cachean, porque no cambian.
 * En un rango de 3 meses eso significa que dos de las tres peticiones
 * desaparecen a partir de la segunda visita.
 *
 * SIEMPRE SECUENCIAL. Lanzar las ventanas en paralelo encima las rafagas de
 * CPU y es peor que una sola consulta grande.
 */

/** Divide [desde, hasta] en ventanas de un mes calendario. */
export const dividirEnMeses = (desde, hasta) => {
    const inicio = moment(desde).startOf("day");
    const fin = moment(hasta).endOf("day");

    if (!inicio.isValid() || !fin.isValid() || fin.isBefore(inicio)) {
        return [];
    }

    const ventanas = [];
    let cursor = inicio.clone();

    while (cursor.isSameOrBefore(fin)) {
        const finDeMes = cursor.clone().endOf("month");
        const finVentana = finDeMes.isAfter(fin) ? fin.clone() : finDeMes;

        ventanas.push({
            desde: cursor.clone(),
            hasta: finVentana.clone(),
            etiqueta: cursor.format("YYYY-MM"),
        });

        cursor = finVentana.clone().add(1, "day").startOf("day");
    }

    return ventanas;
};

/** Una ventana es cacheable solo si terminó antes del mes en curso. */
const esVentanaCerrada = (ventana) =>
    ventana.hasta.isBefore(moment().startOf("month"));

/**
 * @param {object}   opciones
 * @param {string}   opciones.url        endpoint base (p. ej. VentaFiltro)
 * @param {object}   opciones.filtros    pares clave/valor extra (clienteId, empleadoId, estado...)
 * @param {string}   opciones.desde      YYYY-MM-DD
 * @param {string}   opciones.hasta      YYYY-MM-DD
 * @param {string}   opciones.familia    prefijo de cache (p. ej. "ventas")
 * @param {function} [opciones.onLote]   (acumulado, info) => void — render progresivo
 * @param {AbortSignal} [opciones.signal]
 * @param {number}   [opciones.timeout]  ms por ventana (por defecto 45000)
 * @returns {Promise<Array>} todos los registros del rango
 */
export const cargarPorRangos = async ({
    url,
    filtros = {},
    desde,
    hasta,
    familia = "datos",
    onLote,
    signal,
    timeout = 45000,
}) => {
    const ventanas = dividirEnMeses(desde, hasta);
    if (ventanas.length === 0) return [];

    // Los filtros entran en la clave de cache: distinto filtro, distinto resultado.
    const filtrosOrdenados = Object.keys(filtros)
        .filter((k) => filtros[k] !== undefined && filtros[k] !== null && filtros[k] !== "")
        .sort()
        .map((k) => `${k}=${filtros[k]}`)
        .join("&");

    let acumulado = [];

    for (let i = 0; i < ventanas.length; i++) {
        if (signal?.aborted) {
            const e = new Error("Carga cancelada");
            e.name = "CanceledError";
            throw e;
        }

        const ventana = ventanas[i];
        const clave = `${familia}|${ventana.etiqueta}|${filtrosOrdenados}`;
        const cacheable = esVentanaCerrada(ventana);

        let registros = null;
        let deCache = false;

        if (cacheable) {
            registros = await leerLote(clave);
            deCache = registros !== null;
        }

        if (registros === null) {
            const params = new URLSearchParams();

            Object.keys(filtros).forEach((k) => {
                const v = filtros[k];
                if (v !== undefined && v !== null && v !== "") {
                    params.append(k, v);
                }
            });

            params.append(
                "fechaInicio",
                ventana.desde.clone().startOf("day").format("YYYY-MM-DD HH:mm:ss")
            );
            params.append(
                "fechaFin",
                ventana.hasta.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss")
            );

            const respuesta = await AxiosPrivado.get(
                `${url}?${params.toString()}`,
                { timeout, signal }
            );

            registros = Array.isArray(respuesta.data) ? respuesta.data : [];

            if (cacheable && registros.length > 0) {
                // No se espera: que la cache falle no debe frenar la carga.
                guardarLote(clave, registros);
            }
        }

        acumulado = acumulado.concat(registros);

        if (typeof onLote === "function") {
            onLote(acumulado, {
                indice: i + 1,
                total: ventanas.length,
                etiqueta: ventana.etiqueta,
                deCache,
                registrosVentana: registros.length,
            });
        }
    }

    return acumulado;
};

/** Texto de progreso para el indicador de carga. */
export const textoProgreso = (info) => {
    const mes = moment(info.etiqueta, "YYYY-MM").format("MMMM YYYY");
    const origen = info.deCache ? " (en cache)" : "";
    return `Cargando ${mes}${origen} — ${info.indice} de ${info.total}`;
};
